import { concat } from '@ember/helper';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { local } from 'embroider-css-modules';

import styles from './bullet-graph.module.css';

interface PrismBulletGraphSignature {
  Args: {
    comparison?: number;
    // `current` is null when data is missing
    current?: number | null;
    invertDifferenceColor?: boolean;
    label: string;
    mark?: number;
    // `max` represents the highest value that a group of graphs can have
    max?: number;
    symbol: string;
  };
}

export default class PrismBulletGraph extends Component<PrismBulletGraphSignature> {
  get classForDifference(): 'is-negative' | 'is-positive' | undefined {
    const { invertDifferenceColor } = this.args;
    const { difference } = this;

    if (difference === 0) {
      return undefined;
    }

    if (invertDifferenceColor) {
      return difference > 0 ? 'is-negative' : 'is-positive';
    }

    return difference > 0 ? 'is-positive' : 'is-negative';
  }

  get comparisonValue(): number {
    return this.args.comparison ?? 0;
  }

  get currentValue(): number {
    return this.args.current ?? 0;
  }

  get difference(): number {
    if (this.comparisonValue === 0) {
      return 0;
    }

    return this.currentValue - this.comparisonValue;
  }

  get formattedDifference(): string {
    const { symbol } = this.args;
    const { difference } = this;

    if (difference === 0) {
      return '';
    }

    return difference > 0
      ? `+${difference}${symbol}`
      : `${difference}${symbol}`;
  }

  get isDataMissing(): boolean {
    return this.args.current === null;
  }

  get markValue(): number {
    return this.args.mark ?? 0;
  }

  @cached get maxValue(): number {
    const { max } = this.args;

    const maximumValue = Math.max(
      this.comparisonValue,
      this.currentValue,
      this.markValue,
    );

    if (max === undefined) {
      return maximumValue;
    }

    return Math.max(max, maximumValue);
  }

  get styleForComparisonValue(): string {
    const comparisonProportion = this.comparisonValue / this.maxValue;

    return `width: ${100 * comparisonProportion}%;`;
  }

  get styleForCurrentValue(): string {
    const currentProportion = this.currentValue / this.maxValue;

    return currentProportion
      ? `width: ${100 * currentProportion}%;`
      : 'width: 5px';
  }

  get styleForMarkValue(): string {
    const markProportion = this.markValue / this.maxValue;

    return `width: ${100 * markProportion}%;`;
  }

  <template>
    <div class={{styles.container}}>
      <p class={{styles.label}}>
        {{@label}}
      </p>

      <div class={{styles.content}}>
        {{#if this.isDataMissing}}
          <div class={{styles.bars}}></div>
        {{else}}
          <div class={{styles.bars}}>
            {{#if this.comparisonValue}}
              <div
                class={{styles.comparison-bar}}
                data-test-bar="comparison"
                style={{this.styleForComparisonValue}}
              ></div>
            {{/if}}

            <div
              class={{styles.current-bar}}
              data-test-bar="current"
              style={{this.styleForCurrentValue}}
            ></div>

            {{#if this.markValue}}
              <div
                class={{styles.mark-bar}}
                data-test-bar="mark"
                style={{this.styleForMarkValue}}
              ></div>
            {{/if}}
          </div>

          <p class={{styles.current-value}} data-test-value="current">
            {{concat this.currentValue @symbol}}
          </p>

          {{#if this.difference}}
            <p
              class={{local styles "difference" this.classForDifference}}
              data-test-value="difference"
            >
              {{this.formattedDifference}}
            </p>
          {{/if}}
        {{/if}}
      </div>
    </div>
  </template>
}
